import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import eventEmitter, { types } from '../../shared/eventEmitter'
import useDialogDismiss from '../../shared/useDialogDismiss'
import Btn from '../Btn'

import styles from './style.module.scss'

/*
 * Die eine Rueckfrage der Anwendung.
 *
 * Sie haengt einmal in App.js und wird nicht als Bauteil eingesetzt, sondern
 * gerufen: `askConfirm(...)` in shared/confirmDialog liefert ein Versprechen,
 * das mit true oder false eingeloest wird. So laesst sich die Frage auch aus
 * einer Ablaufkette heraus stellen ("beim Schliessen, falls ungespeichert"),
 * ohne dass die aufrufende Ansicht dafuer einen eigenen Zustand fuehren muss.
 *
 * Der Zwischenfall, den es zu vermeiden gilt: Wer ein Fenster daneben anklickt,
 * verliert sonst stillschweigend alles Eingetippte.
 */
export default function ConfirmDialog() {
	const { t } = useTranslation()
	const [request, setRequest] = useState(null)
	/*
	 * Manche Rueckfragen brauchen eine Begruendung — etwa bevor eine Lehrkraft
	 * den Nachweis einer Schueler:in entfernt (Anforderung #94). Dann loest die
	 * Frage nicht mit true auf, sondern mit dem eingetippten Text; abgebrochen
	 * bleibt false. Ein eigenes Fenster dafuer waere das zweite Bauteil fuer
	 * dieselbe Sache.
	 */
	const [reason, setReason] = useState('')
	const [touched, setTouched] = useState(false)

	useEffect(() => {
		const handleOpen = payload => {
			setReason('')
			setTouched(false)
			setRequest(payload)
		}

		eventEmitter.on(types.openConfirmDialog, handleOpen)
		return () => eventEmitter.off(types.openConfirmDialog, handleOpen)
	}, [])

	const answer = value => {
		if (request) request.resolve(value)
		setRequest(null)
	}

	// Zusagen heisst bei einer Frage mit Feld: den Text zurueckgeben. Ist er
	// leer, bleibt das Fenster stehen und sagt, was fehlt.
	const confirm = () => {
		if (!request?.prompt) return answer(true)

		setTouched(true)
		if (!reason.trim()) return

		answer(reason.trim())
	}

	// Klick daneben und Esc gelten als "nein" — die zurueckhaltende Antwort.
	// Bei der Frage nach ungespeicherten Inhalten heisst das: weiter bearbeiten.
	const backdropProps = useDialogDismiss(() => answer(false), { enabled: Boolean(request) })

	if (!request) return null

	return (
		<div className={styles.backdrop} {...backdropProps}>
			<div className={styles.dialog} role="alertdialog" aria-modal="true">
				<h2 className={styles.title}>{request.title}</h2>
				{request.text && <p className={styles.text}>{request.text}</p>}

				{request.prompt && (
					<label className={styles.field}>
						<span className={styles.fieldLabel}>{request.prompt.label}</span>
						<textarea
							className={styles.input}
							rows={3}
							maxLength={500}
							autoFocus
							placeholder={request.prompt.placeholder || ''}
							value={reason}
							onChange={event => setReason(event.target.value)}
						/>
						{touched && !reason.trim() && (
							<span className={styles.fieldError}>
								{request.prompt.error || t('Please give a reason.')}
							</span>
						)}
					</label>
				)}

				<div className={styles.actions}>
					<Btn type="button" theme="secondary" onClick={() => answer(false)}>
						{request.cancelLabel || t('Cancel')}
					</Btn>
					<Btn
						type="button"
						theme={request.tone === 'danger' ? 'danger' : 'primary'}
						onClick={confirm}
					>
						{request.confirmLabel || t('OK')}
					</Btn>
				</div>
			</div>
		</div>
	)
}
